'use client';

import { FC } from 'react';
import PrismAPI from '@modules/prism-client/types/prism-types';
import { InputField } from '@tuic/web-compositions/shared/InputField';
import { SelectField } from '@tuic/web-compositions/shared/SelectField';
import { CheckboxField } from '@tuic/web-compositions/shared/CheckboxField';
import { RadioButtonField } from '@tuic/web-compositions/shared/RadioButtonField';
export interface FormFieldProps {
  data: PrismAPI.FormField;
  onChange?: (name: string, value: string | boolean) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * Dynamic form field renderer that maps Prism API FormField types
 * to their corresponding @tuic/web-compositions components.
 *
 * Uses discriminated union pattern based on field.type to render
 * the appropriate design system field component directly.
 *
 * Form tracking is handled by TrackFormAttribution component via
 * native HTML name attributes and global event listeners.
 */
export const FormField: FC<FormFieldProps> = ({
  data,
  onChange,
  error,
  disabled
}) => {
  if (data.type === 'input') {
    return <InputField name={data.name} type={data.inputType ?? 'text'} label={data.label} note={data.note} placeholder={data.placeholder} defaultValue={data.value} onChange={e => onChange?.(data.name, e.target.value)} required={data.required} disabled={disabled ?? data.disabled} error={error} autoComplete={data.autocomplete} />;
  }
  if (data.type === 'select') {
    return <SelectField name={data.name} label={data.label} note={data.note} defaultValue={data.value} onChange={e => onChange?.(data.name, e.target.value)} required={data.required} disabled={disabled ?? data.disabled} error={error}>
        {data.placeholder && <option value="" disabled>
            {data.placeholder}
          </option>}
        {data.options.map(option => <option key={option.value} defaultValue={option.value}>
            {option.label}
          </option>)}
      </SelectField>;
  }
  if (data.type === 'checkbox') {
    return <CheckboxField name={data.name} label={data.label} note={data.note} checked={data.checked} defaultValue={data.value} onChange={e => onChange?.(data.name, e.target.checked)} required={data.required} disabled={disabled ?? data.disabled} error={error} />;
  }
  if (data.type === 'radio') {
    return <RadioButtonField name={data.name} defaultValue={data.value} label={data.label} note={data.note} checked={data.checked} onChange={e => onChange?.(data.name, e.target.checked)} disabled={disabled ?? data.disabled} required={data.required} error={error} />;
  }

  // Exhaustive check - TypeScript will error if we miss a field type
  const _exhaustiveCheck: never = data;
  return null;
};